import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import { Map as MapIcon, MapPin, RotateCcw, Route, Compass } from "lucide-react";
// @ts-expect-error -- leaflet lacks bundled types; @types/leaflet can be added as devDep
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { t } from "@/lib/i18n/messages";
import type {
  InitiativeLocation,
  InitiativePathResponse,
} from "@/types/initiative";
import type { Locale } from "@/types/auth";
import { cn } from "@/lib/utils";

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

interface InitiativeLocationMapProps {
  locations: InitiativeLocation[];
  paths: InitiativePathResponse[];
  walkingAreaType?: string;
  locale: Locale;
}

const PATH_COLORS = [
  "#f97316",
  "#3b82f6",
  "#10b981",
  "#8b5cf6",
  "#ef4444",
  "#06b6d4",
];

function FitBounds({
  bounds,
  registerRecenter,
}: {
  bounds: L.LatLngBoundsExpression | null;
  registerRecenter: (fn: () => void) => void;
}) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
    }
  }, [bounds, map]);

  useEffect(() => {
    registerRecenter(() => {
      if (bounds) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
    });
  }, [bounds, map, registerRecenter]);

  return null;
}

export function InitiativeLocationMap({
  locations,
  paths,
  walkingAreaType,
  locale,
}: InitiativeLocationMapProps) {
  const isRTL = locale === "ar";

  const bounds = useMemo(() => {
    const points: [number, number][] = [];
    locations.forEach((loc) => {
      if (loc.latitude && loc.longitude) {
        points.push([loc.latitude, loc.longitude]);
      }
    });
    for (const path of paths) {
      const segs = path.segments ?? [];
      for (const seg of segs) {
        if (seg.start_lat != null && seg.start_lng != null) {
          points.push([seg.start_lat, seg.start_lng]);
        }
        if (seg.end_lat != null && seg.end_lng != null) {
          points.push([seg.end_lat, seg.end_lng]);
        }
      }
    }
    if (points.length === 0) return null;
    const first = points[0];
    if (!first) return null;
    if (points.length === 1) {
      return L.latLngBounds(
        [first[0] - 0.005, first[1] - 0.005],
        [first[0] + 0.005, first[1] + 0.005],
      );
    }
    return L.latLngBounds(points);
  }, [locations, paths]);

  const hasContent = locations.length > 0 || paths.length > 0;
  const defaultCenter: [number, number] = useMemo(() => {
    const firstLoc = locations[0];
    if (firstLoc) return [firstLoc.latitude, firstLoc.longitude];
    const firstPath = paths?.[0];
    const firstSeg = firstPath?.segments?.[0];
    if (firstSeg) return [firstSeg.start_lat, firstSeg.start_lng];
    return [24.7136, 46.6753];
  }, [locations, paths]);

  const recenterRef = useRef<(() => void) | null>(null);
  const registerRecenter = useCallback((fn: () => void) => {
    recenterRef.current = fn;
  }, []);
  const handleRecenter = useCallback(() => {
    recenterRef.current?.();
  }, []);

  if (!hasContent) {
    return (
      <div className="rounded-2xl border border-border bg-white p-10 md:p-12 text-center space-y-4">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-blue/10 text-brand-blue">
          <MapIcon className="w-6 h-6" strokeWidth={1.8} />
        </div>
        <div className="font-sans text-[11px] tracking-[0.25em] text-foreground-secondary uppercase">
          {isRTL ? "الخريطة غير متوفّرة" : "MAP UNAVAILABLE"}
        </div>
        <p className="text-[14px] text-foreground-secondary max-w-md mx-auto">
          {t(locale, "noLocations")}
        </p>
      </div>
    );
  }

  const primaryAddress =
    locations[0]?.name ||
    locations[0]?.address ||
    (isRTL ? "السعودية" : "Saudi Arabia");

  return (
    <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="relative">
        {/* top vignette */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/15 to-transparent z-[400]"
        />

        {/* floating mono address pill */}
        <div className="pointer-events-none absolute top-3 start-3 z-[500] flex items-center gap-2">
          <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white/95 shadow-sm backdrop-blur-sm font-sans text-[10px] tracking-[0.2em] uppercase text-foreground">
            <MapPin className="w-3.5 h-3.5 text-brand-blue" strokeWidth={2} />
            <span className="truncate max-w-[220px]">{primaryAddress}</span>
          </span>
          {walkingAreaType && (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 h-8 px-3 rounded-full font-sans text-[10px] tracking-[0.2em] uppercase shadow-sm backdrop-blur-sm",
                "bg-brand-blue/10 text-brand-blue",
              )}
            >
              {walkingAreaType === "path" ? (
                <Route className="w-3 h-3" strokeWidth={2} />
              ) : (
                <Compass className="w-3 h-3" strokeWidth={2} />
              )}
              <span>
                {walkingAreaType === "path"
                  ? t(locale, "structuredPath")
                  : t(locale, "openExploration")}
              </span>
            </span>
          )}
        </div>

        {/* recenter button */}
        <button
          type="button"
          onClick={handleRecenter}
          aria-label={isRTL ? "إعادة التمركز" : "Recenter map"}
          className="absolute bottom-3 end-3 z-[500] inline-flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm border border-border text-foreground hover:bg-muted transition-colors active:scale-95"
        >
          <RotateCcw className="w-4 h-4" strokeWidth={2} />
        </button>

        <div className="h-[320px] w-full">
          <MapContainer
            {...({
              center: defaultCenter,
              zoom: 14,
              className: "h-full w-full z-0",
              scrollWheelZoom: false,
            } as any)}
          >
            <TileLayer
              {...({
                attribution:
                  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
              } as any)}
            />

            <FitBounds bounds={bounds} registerRecenter={registerRecenter} />

            {locations.map((loc) => (
              <Marker key={loc.id} position={[loc.latitude, loc.longitude]}>
                <Popup>
                  <div className="text-sm">
                    {loc.name && <strong>{loc.name}</strong>}
                    {loc.address && (
                      <p className="text-xs text-gray-500 mt-1">
                        {loc.address}
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {paths.map((path, pathIdx) => {
              const color = PATH_COLORS[pathIdx % PATH_COLORS.length];
              const positions: [number, number][] = [];
              (path.segments ?? []).forEach((seg) => {
                if (positions.length === 0) {
                  positions.push([seg.start_lat, seg.start_lng]);
                }
                positions.push([seg.end_lat, seg.end_lng]);
              });
              if (positions.length < 2) return null;
              return (
                <Polyline
                  key={path.id}
                  {...({
                    positions,
                    pathOptions: { color, weight: 4, opacity: 0.85 },
                  } as any)}
                />
              );
            })}
          </MapContainer>
        </div>
      </div>

      {/* footer legend */}
      <div className="px-5 md:px-6 py-3 border-t border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {paths.map((path, idx) => {
            const color = PATH_COLORS[idx % PATH_COLORS.length];
            const name =
              locale === "ar"
                ? path.name_ar || path.name_en
                : path.name_en || path.name_ar;
            return (
              <div
                key={path.id}
                className="inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.18em] uppercase text-foreground-secondary"
              >
                <span
                  className="w-3 h-1 rounded"
                  style={{ backgroundColor: color }}
                />
                <span className="text-foreground">
                  {name || `Path ${idx + 1}`}
                </span>
                <span className="tabular-nums">
                  {Math.round(path.total_distance_meters)}
                  {t(locale, "meters")} · {path.segments_count}{" "}
                  {t(locale, "segments")}
                </span>
              </div>
            );
          })}
        </div>
        <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-foreground-secondary tabular-nums">
          <span>
            {t(locale, "locationsCount")}: {locations.length}
          </span>
          {paths.length > 0 && (
            <span className="ms-3">
              {t(locale, "pathsCount")}: {paths.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
